import { useState, useEffect, useRef } from 'react';
import useDelete from './useDelete';
import useCreate from '../useCreate';
import toast from 'react-hot-toast';

function useProductActions(productListData) {
  // Delete modal states
  const [productId, setProductId] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  
  // Document modal states
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDocuments, setProductDocuments] = useState([]);
  const [isDocumentModalOpen, setDocumentModalOpen] = useState(false);
  
  // Add a state to track the document being deleted
  const [currentDeletingMapId, setCurrentDeletingMapId] = useState(null);
  
  // Add a ref to track current position input
  const positionInputRef = useRef({ element: null, originalValue: null });
  
  // Add a ref to track the current document being toggled
  const currentDocumentId = useRef(null);

  // Function to refresh modal data from the product list
  const refreshModalWithLatestData = () => {
    if (selectedProduct && isDocumentModalOpen && productListData?.data?.data) {
      // Find the current product in the refreshed product list
      const updatedProduct = productListData.data.data.find(
        product => product.product_id === selectedProduct.product_id
      );
      
      if (updatedProduct) {
        // Update the modal state with fresh data
        setSelectedProduct(updatedProduct);
        setProductDocuments(updatedProduct.product_documents || []);
      }
    }
  };

  // Watch for changes in productListData to refresh modal when API data changes
  useEffect(() => {
    refreshModalWithLatestData();
  }, [productListData?.data]);
  
  // Delete product API handler
  const { handleDelete: onDeleteProduct } = useDelete({
    url: 'delete-product',
    refreshUrl: 'get-product-list',
    successToastMessage: 'Product Deleted Successfully',
    errorToastMessage: 'Failed to delete product.',
    onSuccess: () => {
      setDeleteModalOpen(false);
    }
  });

  // Update document mapping API handler - Add refreshUrl
  const { setDataToServer: updateDocumentMap } = useCreate({
    url: "update-product-document-map",
    refreshUrl: "get-product-list", // Add this to refresh product list
    onSuccess: () => {
      toast.success("Document position updated", {
        position: "top-right",
        duration: 3000,
      });
      // Clear the reference after success
      positionInputRef.current = { element: null, originalValue: null };
    },
    onError: (err) => {
      toast.error(`Error updating document: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
      
      // Reset input field to its original value when API call fails
      if (positionInputRef.current.element && positionInputRef.current.originalValue !== null) {
        positionInputRef.current.element.value = positionInputRef.current.originalValue;
      }
      
      // Clear the reference
      positionInputRef.current = { element: null, originalValue: null };
    },
  });

  // Delete document mapping
  const { setDataToServer: deleteDocumentMap } = useCreate({
    url: "delete-product-document-map",
    refreshUrl: "get-product-list",
    onSuccess: () => {
      toast.success("Document removed successfully", {
        position: "top-right", 
        duration: 3000,
      });
      // Reset the tracking state after successful deletion
      setCurrentDeletingMapId(null);
      // No need to manually update state - refreshModalWithLatestData will handle it
    },
    onError: (err) => {
      // Reset the tracking state on error as well
      setCurrentDeletingMapId(null);
      toast.error(`Error removing document: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  // Toggle document visibility API handler - Fixed to correctly display the visibility status
  const { setDataToServer: toggleDocumentVisibility } = useCreate({
    url: "show-product-document-map",
    refreshUrl: "get-product-list",
    onSuccess: (data) => {
      const updatedDocument = productDocuments.find(doc => 
        doc.product_document_map_id === currentDocumentId.current);
      
      // If document found, determine the new status based on its current state
      if (updatedDocument) {
        const visibilityStatus = updatedDocument.active === "1" ? "hidden" : "visible";
        toast.success(`Document is now ${visibilityStatus}`, {
          position: "top-right",
          duration: 3000,
        });
      } else {
        // Generic success message as fallback
        toast.success("Document visibility updated", {
          position: "top-right",
          duration: 3000,
        });
      }
      
      // Clear the tracking reference
      currentDocumentId.current = null;
    },
    onError: (err) => {
      // Clear the tracking reference on error
      currentDocumentId.current = null;
      toast.error(`Error updating document visibility: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  // Add status toggle API handler
  const { setDataToServer: toggleProductStatus } = useCreate({
    url: "product-status",
    refreshUrl: "get-product-list",
    onSuccess: (data) => {
      toast.success("Product status updated successfully", {
        position: "top-right",
        duration: 3000,
      });
    },
    onError: (err) => {
      toast.error(`Error updating product status: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  // Handle status toggle
  const handleStatusToggle = (productId, currentStatus) => {
    // Convert active value (0/1 string) to boolean and toggle it
    const newStatus = currentStatus === "1" ? false : true;
    
    toggleProductStatus.mutate({
      product_id: productId,
      status: newStatus
    });
  };

  // Delete product handlers
  const handleDelete = (id) => {
    setProductId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => setDeleteModalOpen(false);

  const confirmDelete = () => onDeleteProduct({ product_id: productId });

  // Document modal handlers
  const handleOpenDocumentModal = (product) => {
    setSelectedProduct(product);
    setProductDocuments(product.product_documents || []);
    setDocumentModalOpen(true);
  };

  const handleCloseDocumentModal = () => {
    setDocumentModalOpen(false);
    setSelectedProduct(null);
    setProductDocuments([]);
  };

  // Document management handlers
  const handleRemoveDocument = (mapId) => {
    // Store the mapId before making the API call
    setCurrentDeletingMapId(mapId);
    
    deleteDocumentMap.mutate({
      product_document_map_id: mapId
    });
  };

  const handlePositionChange = (e, doc) => {
    const inputElement = e.target;
    const originalPosition = doc.position;
    const newPosition = parseInt(inputElement.value, 10);
    const currentPosition = parseInt(originalPosition, 10);
    
    // Basic validation - must be a number
    if (isNaN(newPosition) || newPosition <= 0) {
      toast.error("Position must be a positive number", {
        position: "top-right",
        duration: 3000,
      });
      // Reset to current position
      inputElement.value = originalPosition;
      return;
    }
    
    // Only update if position changed
    if (newPosition !== currentPosition) {
      // Store reference to input element and original value
      positionInputRef.current = {
        element: inputElement,
        originalValue: originalPosition
      };
      
      updateDocumentMap.mutate({
        product_document_map_id: doc.product_document_map_id,
        product_id: doc.product_id,
        document_id: doc.document_id,
        position: newPosition
      });
    } else {
      // Position hasn't changed, no need to call API
      console.log("Position unchanged, skipping update");
    }
  };

  // Update handler to use 'show_image' parameter with "true" or "false" values
  const handleToggleVisibility = (mapId, isCurrentlyActive) => {
    // Store the document ID before making the API call
    currentDocumentId.current = mapId;
    
    // Convert from active "0"/"1" to show_image "true"/"false"
    // If active is "1" make show_image "false" (to toggle it off)
    // If active is "0" make show_image "true" (to toggle it on)
    const newVisibilityState = isCurrentlyActive ? "false" : "true";
    
    toggleDocumentVisibility.mutate({
      product_document_map_id: mapId,
      show_image: newVisibilityState
    });
  };

  return {
    // Delete product
    productId,
    isDeleteModalOpen,
    handleDelete,
    closeDeleteModal,
    confirmDelete,
    
    // Document management
    selectedProduct,
    productDocuments,
    isDocumentModalOpen,
    handleOpenDocumentModal,
    handleCloseDocumentModal,
    handleRemoveDocument,
    handlePositionChange,
    handleToggleVisibility,
    refreshModalWithLatestData,
    handleStatusToggle,
    isTogglingStatus: toggleProductStatus.isLoading
  };
}

export default useProductActions;
